import {useEffect, useState} from 'react'
import {useQuery, useMutation} from '@apollo/client'
import {Table, Button, Form, Spinner, Card} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {
  GET_ONBOARDING_CONFIG_BY_ORGANIZATION,
  GET_SIGNATURE_FORMS_BY_ORGANIZATION,
} from '../../../gql/queries/organizationOnboardingQuery'
import {
  CREATE_ONBOARDING_STEP_MESSAGE,
  UPDATE_ONBOARDING_STEP_MESSAGE,
  DELETE_ONBOARDING_STEP_MESSAGE,
  CREATE_REQUIRED_DOCUMENT_CONFIG,
  UPDATE_REQUIRED_DOCUMENT_CONFIG,
  DELETE_REQUIRED_DOCUMENT_CONFIG,
  UPDATE_ORGANIZATION_ONBOARDING_SIGNATURE_FORMS,
} from '../../../gql/mutations/organizationOnboardingMutations'
import {parseId} from '../../../helpers'
import {ModalAddSignatureForm} from '../../signatureForms/components/ModalAddSignatureForm'

interface Props {
  organizationId: string
}

const STEP_KEYS = [
  {value: 'personal_info', label: 'Personal Information'},
  {value: 'licensing', label: 'Licensing'},
  {value: 'professional_info', label: 'Professional Information'},
]

const DOCUMENT_TYPES = [
  {value: 'governmentId', label: 'Government ID'},
  {value: 'insuranceLicense', label: 'Insurance License'},
  {value: 'w9', label: 'W-9'},
  {value: 'eoCertificate', label: 'E&O Certificate'},
  {value: 'bankDocumentation', label: 'Bank Documentation'},
  {value: 'agencyDocumentation', label: 'Agency Documentation'},
]

const DOCUMENT_CONTEXTS = [
  {value: 'licensed_yes', label: 'Licensed - Yes'},
  {value: 'licensed_in_process', label: 'Licensed - In Process'},
  {value: 'agency', label: 'Agency'},
  {value: 'individual', label: 'Individual'},
  {value: 'general', label: 'General'},
]

const stepKeyLabel = (value: string) => STEP_KEYS.find((s) => s.value === value)?.label || value
const documentTypeLabel = (value: string) => DOCUMENT_TYPES.find((d) => d.value === value)?.label || value
const documentContextLabel = (value: string) => DOCUMENT_CONTEXTS.find((c) => c.value === value)?.label || value

interface IStepMessage {
  id: string
  stepKey: string
  message: string
  isActive: boolean
}

interface IRequiredDocument {
  id: string
  documentType: string
  context: string
  isRequired: boolean
}

interface ISignatureForm {
  id: string
  name: string
}

const EMPTY_STEP_FORM = {stepKey: STEP_KEYS[0].value, message: '', isActive: true}
const EMPTY_DOC_FORM = {documentType: DOCUMENT_TYPES[0].value, context: DOCUMENT_CONTEXTS[0].value, isRequired: true}

export const OrganizationOnboardingTab: React.FC<Props> = ({organizationId}) => {
  const {data, loading, refetch} = useQuery(GET_ONBOARDING_CONFIG_BY_ORGANIZATION, {
    variables: {organization: organizationId},
    fetchPolicy: 'network-only',
  })

  const config = data?.onboardingConfigByOrganization
  const stepMessages: IStepMessage[] = config?.stepMessages || []
  const requiredDocuments: IRequiredDocument[] = config?.requiredDocuments || []
  const selectedSignatureFormIds: string[] = (config?.signatureForms || []).map((f: ISignatureForm) => f.id)

  return (
    <div>
      <Card className="mb-5">
        <Card.Body>
          <h5 className="fw-bold mb-3">Step Messages</h5>
          <StepMessagesPanel
            organizationId={organizationId}
            stepMessages={stepMessages}
            loading={loading}
            refetch={refetch}
          />
        </Card.Body>
      </Card>

      <Card className="mb-5">
        <Card.Body>
          <h5 className="fw-bold mb-3">Required Documents</h5>
          <RequiredDocumentsPanel
            organizationId={organizationId}
            requiredDocuments={requiredDocuments}
            loading={loading}
            refetch={refetch}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <h5 className="fw-bold mb-3">Agreements/Consents</h5>
          <AgreementsPanel
            organizationId={organizationId}
            selectedSignatureFormIds={selectedSignatureFormIds}
            refetch={refetch}
          />
        </Card.Body>
      </Card>
    </div>
  )
}

const StepMessagesPanel: React.FC<{
  organizationId: string
  stepMessages: IStepMessage[]
  loading: boolean
  refetch: () => void
}> = ({organizationId, stepMessages, loading, refetch}) => {
  const [createStepMessage] = useMutation(CREATE_ONBOARDING_STEP_MESSAGE)
  const [updateStepMessage] = useMutation(UPDATE_ONBOARDING_STEP_MESSAGE)
  const [deleteStepMessage] = useMutation(DELETE_ONBOARDING_STEP_MESSAGE)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({...EMPTY_STEP_FORM})
  const [showNewRow, setShowNewRow] = useState(false)
  const [newForm, setNewForm] = useState({...EMPTY_STEP_FORM})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const startEdit = (row: IStepMessage) => {
    setEditingId(row.id)
    setEditForm({stepKey: row.stepKey, message: row.message, isActive: row.isActive})
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({...EMPTY_STEP_FORM})
  }

  const handleCreate = async () => {
    setSaving(true)
    try {
      const res = await createStepMessage({
        variables: {
          stepMessageData: {
            organizationConfig: organizationId,
            stepKey: newForm.stepKey,
            message: newForm.message,
            isActive: newForm.isActive,
          },
        },
      })
      const errors = res.data?.createOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Step message created')
        setNewForm({...EMPTY_STEP_FORM})
        setShowNewRow(false)
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error creating step message')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    setSaving(true)
    try {
      const res = await updateStepMessage({
        variables: {
          id: parseId(id),
          stepMessageData: {
            organizationConfig: organizationId,
            stepKey: editForm.stepKey,
            message: editForm.message,
            isActive: editForm.isActive,
          },
        },
      })
      const errors = res.data?.updateOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Step message updated')
        cancelEdit()
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error updating step message')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this step message?')) return
    setDeletingId(id)
    try {
      const res = await deleteStepMessage({variables: {id: parseId(id)}})
      const errors = res.data?.deleteOnboardingStepMessage?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Step message deleted')
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error deleting step message')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        {!showNewRow && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowNewRow(true)}>
            <i className="bi bi-plus-lg me-1"></i>
            Add Step Message
          </Button>
        )}
      </div>

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th style={{width: '20%'}}>Step</th>
            <th style={{width: '45%'}}>Message</th>
            <th style={{width: '10%'}}>Active</th>
            <th style={{width: '25%'}} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {showNewRow && (
            <tr>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.stepKey}
                  onChange={(e) => setNewForm((p) => ({...p, stepKey: e.target.value}))}
                >
                  {STEP_KEYS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Control
                  as="textarea"
                  rows={2}
                  size="sm"
                  placeholder="message"
                  value={newForm.message}
                  onChange={(e) => setNewForm((p) => ({...p, message: e.target.value}))}
                />
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={newForm.isActive}
                  onChange={(e) => setNewForm((p) => ({...p, isActive: e.target.checked}))}
                />
              </td>
              <td className="text-end">
                <Button size="sm" variant="success" className="me-1" onClick={handleCreate} disabled={saving}>
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewRow(false)
                    setNewForm({...EMPTY_STEP_FORM})
                  }}
                  disabled={saving}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </td>
            </tr>
          )}
          {stepMessages.length === 0 && !showNewRow && (
            <tr>
              <td colSpan={4} className="text-center text-muted py-4">
                No step messages configured for this organization yet.
              </td>
            </tr>
          )}
          {stepMessages.map((row) => (
            <tr key={row.id}>
              {editingId === row.id ? (
                <>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.stepKey}
                      onChange={(e) => setEditForm((p) => ({...p, stepKey: e.target.value}))}
                    >
                      {STEP_KEYS.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      size="sm"
                      value={editForm.message}
                      onChange={(e) => setEditForm((p) => ({...p, message: e.target.value}))}
                    />
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={editForm.isActive}
                      onChange={(e) => setEditForm((p) => ({...p, isActive: e.target.checked}))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="success" className="me-1" onClick={() => handleUpdate(row.id)} disabled={saving}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button size="sm" variant="light" onClick={cancelEdit} disabled={saving}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>{stepKeyLabel(row.stepKey)}</td>
                  <td style={{whiteSpace: 'pre-wrap'}}>{row.message}</td>
                  <td>{row.isActive ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                  <td className="text-end">
                    <Button size="sm" variant="light" className="me-1" onClick={() => startEdit(row)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="text-danger"
                      onClick={() => handleDelete(row.id)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const RequiredDocumentsPanel: React.FC<{
  organizationId: string
  requiredDocuments: IRequiredDocument[]
  loading: boolean
  refetch: () => void
}> = ({organizationId, requiredDocuments, loading, refetch}) => {
  const [createRequiredDocument] = useMutation(CREATE_REQUIRED_DOCUMENT_CONFIG)
  const [updateRequiredDocument] = useMutation(UPDATE_REQUIRED_DOCUMENT_CONFIG)
  const [deleteRequiredDocument] = useMutation(DELETE_REQUIRED_DOCUMENT_CONFIG)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({...EMPTY_DOC_FORM})
  const [showNewRow, setShowNewRow] = useState(false)
  const [newForm, setNewForm] = useState({...EMPTY_DOC_FORM})
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const startEdit = (row: IRequiredDocument) => {
    setEditingId(row.id)
    setEditForm({documentType: row.documentType, context: row.context, isRequired: row.isRequired})
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditForm({...EMPTY_DOC_FORM})
  }

  const handleCreate = async () => {
    setSaving(true)
    try {
      const res = await createRequiredDocument({
        variables: {
          requiredDocumentData: {
            organizationConfig: organizationId,
            documentType: newForm.documentType,
            context: newForm.context,
            isRequired: newForm.isRequired,
          },
        },
      })
      const errors = res.data?.createRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Required document created')
        setNewForm({...EMPTY_DOC_FORM})
        setShowNewRow(false)
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error creating required document')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    setSaving(true)
    try {
      const res = await updateRequiredDocument({
        variables: {
          id: parseId(id),
          requiredDocumentData: {
            organizationConfig: organizationId,
            documentType: editForm.documentType,
            context: editForm.context,
            isRequired: editForm.isRequired,
          },
        },
      })
      const errors = res.data?.updateRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Required document updated')
        cancelEdit()
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error updating required document')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this required document config?')) return
    setDeletingId(id)
    try {
      const res = await deleteRequiredDocument({variables: {id: parseId(id)}})
      const errors = res.data?.deleteRequiredDocumentConfig?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Required document deleted')
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error deleting required document')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        {!showNewRow && (
          <Button size="sm" variant="outline-primary" onClick={() => setShowNewRow(true)}>
            <i className="bi bi-plus-lg me-1"></i>
            Add Required Document
          </Button>
        )}
      </div>

      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th style={{width: '35%'}}>Document Type</th>
            <th style={{width: '30%'}}>Context</th>
            <th style={{width: '10%'}}>Required</th>
            <th style={{width: '25%'}} className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {showNewRow && (
            <tr>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.documentType}
                  onChange={(e) => setNewForm((p) => ({...p, documentType: e.target.value}))}
                >
                  {DOCUMENT_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Select
                  size="sm"
                  value={newForm.context}
                  onChange={(e) => setNewForm((p) => ({...p, context: e.target.value}))}
                >
                  {DOCUMENT_CONTEXTS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Form.Select>
              </td>
              <td>
                <Form.Check
                  type="switch"
                  checked={newForm.isRequired}
                  onChange={(e) => setNewForm((p) => ({...p, isRequired: e.target.checked}))}
                />
              </td>
              <td className="text-end">
                <Button size="sm" variant="success" className="me-1" onClick={handleCreate} disabled={saving}>
                  <i className="bi bi-check-lg"></i>
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => {
                    setShowNewRow(false)
                    setNewForm({...EMPTY_DOC_FORM})
                  }}
                  disabled={saving}
                >
                  <i className="bi bi-x-lg"></i>
                </Button>
              </td>
            </tr>
          )}
          {requiredDocuments.length === 0 && !showNewRow && (
            <tr>
              <td colSpan={4} className="text-center text-muted py-4">
                No required documents configured for this organization yet.
              </td>
            </tr>
          )}
          {requiredDocuments.map((row) => (
            <tr key={row.id}>
              {editingId === row.id ? (
                <>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.documentType}
                      onChange={(e) => setEditForm((p) => ({...p, documentType: e.target.value}))}
                    >
                      {DOCUMENT_TYPES.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={editForm.context}
                      onChange={(e) => setEditForm((p) => ({...p, context: e.target.value}))}
                    >
                      {DOCUMENT_CONTEXTS.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Check
                      type="switch"
                      checked={editForm.isRequired}
                      onChange={(e) => setEditForm((p) => ({...p, isRequired: e.target.checked}))}
                    />
                  </td>
                  <td className="text-end">
                    <Button size="sm" variant="success" className="me-1" onClick={() => handleUpdate(row.id)} disabled={saving}>
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <Button size="sm" variant="light" onClick={cancelEdit} disabled={saving}>
                      <i className="bi bi-x-lg"></i>
                    </Button>
                  </td>
                </>
              ) : (
                <>
                  <td>{documentTypeLabel(row.documentType)}</td>
                  <td>{documentContextLabel(row.context)}</td>
                  <td>{row.isRequired ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                  <td className="text-end">
                    <Button size="sm" variant="light" className="me-1" onClick={() => startEdit(row)}>
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      className="text-danger"
                      onClick={() => handleDelete(row.id)}
                      disabled={deletingId === row.id}
                    >
                      {deletingId === row.id ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
                    </Button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const AgreementsPanel: React.FC<{
  organizationId: string
  selectedSignatureFormIds: string[]
  refetch: () => void
}> = ({organizationId, selectedSignatureFormIds, refetch}) => {
  const {data, loading, refetch: refetchForms} = useQuery(GET_SIGNATURE_FORMS_BY_ORGANIZATION, {
    variables: {organization: organizationId, status: 'ACTIVE'},
    fetchPolicy: 'network-only',
  })
  const [updateSignatureForms] = useMutation(UPDATE_ORGANIZATION_ONBOARDING_SIGNATURE_FORMS)

  const availableForms: ISignatureForm[] = data?.allSignatureForms?.edges?.map((e: any) => e.node) || []

  const [selected, setSelected] = useState<Set<string>>(new Set(selectedSignatureFormIds))
  const [saving, setSaving] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    setSelected(new Set(selectedSignatureFormIds))
  }, [selectedSignatureFormIds.join(',')])

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await updateSignatureForms({
        variables: {organization: organizationId, signatureFormIds: Array.from(selected)},
      })
      const errors = res.data?.updateOrganizationOnboardingSignatureForms?.errors
      if (errors && errors.length > 0) {
        toast.error(errors.map((e: any) => e.message).join('\n'))
      } else {
        toast.success('Agreements updated')
        refetch()
      }
    } catch (err: any) {
      toast.error(err.message || 'Error updating agreements')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner animation="border" size="sm" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <p className="text-muted mb-0" style={{fontSize: '0.8rem'}}>
          Select which signature forms (agreements/consents) this organization requires during onboarding.
        </p>
        <Button variant="outline-primary" size="sm" onClick={() => setShowAddForm(true)}>
          Add Consent
        </Button>
      </div>
      {showAddForm && (
        <ModalAddSignatureForm
          keyboard={false}
          backdrop="static"
          isOpen={showAddForm}
          onClose={() => setShowAddForm(false)}
          refetch={() => {
            refetchForms()
          }}
          idFormEdit={null}
          organizationId={organizationId}
        />
      )}
      {availableForms.length === 0 ? (
        <p className="text-muted text-center py-4">No signature forms available for this organization.</p>
      ) : (
        <div className="mb-4">
          {availableForms.map((form) => (
            <Form.Check
              key={form.id}
              type="checkbox"
              id={`signature-form-${form.id}`}
              label={form.name}
              checked={selected.has(form.id)}
              onChange={() => toggle(form.id)}
              className="mb-2"
            />
          ))}
        </div>
      )}
      <div className="d-flex justify-content-end">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  )
}
